/**
 * Contextual column insertion affordance between board columns.
 *
 * Fixed-width layout spacer (matches former `gap-3`). Line + `+` are an
 * absolutely positioned overlay — opacity only, zero layout shift on hover.
 *
 * `data-armed` on the spacer is the single reveal state: it drives the `+`
 * opacity, the hit region's pointer-events, and the cursor, so nothing is
 * hoverable or clickable before the `+` is on screen. The spacer itself carries
 * the hover-intent listeners (it stays pointer-transparent to the user), and the
 * overlay only renders while insertion is enabled.
 */

import { Plus } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  kanbanColumnInsertAccentVarClass,
  kanbanColumnInsertAffordanceClass,
  kanbanColumnInsertHitClass,
  kanbanColumnInsertIconClass,
  kanbanColumnInsertLineClass,
  kanbanColumnInsertZoneClass,
} from './Kanban.styles';

export interface KanbanColumnInsertZoneProps {
  insertAtIndex: number;
  ariaLabel: string;
  isArmed: boolean;
  disabled?: boolean;
  onPointerIntentStart: () => void;
  onPointerIntentEnd: () => void;
  onAdd: () => void;
}

export function KanbanColumnInsertZone({
  insertAtIndex,
  ariaLabel,
  isArmed,
  disabled = false,
  onPointerIntentStart,
  onPointerIntentEnd,
  onAdd,
}: KanbanColumnInsertZoneProps) {
  const armed = isArmed && !disabled;

  return (
    <div
      className={cn(
        kanbanColumnInsertZoneClass,
        kanbanColumnInsertAccentVarClass
      )}
      data-kanban-column-insert={insertAtIndex}
      data-armed={armed ? 'true' : 'false'}
      onPointerEnter={() => {
        if (disabled) return;
        onPointerIntentStart();
      }}
      onPointerLeave={(event) => {
        if (disabled) return;
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) {
          return;
        }
        onPointerIntentEnd();
      }}
    >
      {disabled ? null : (
        /*
          Overlay hit region: absolute, may extend past the fixed spacer width.
          Does not affect flex layout. Entire region is one insertion control.
        */
        <div
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          data-kanban-column-insert-hit=""
          className={kanbanColumnInsertHitClass}
          onClick={(event) => {
            event.stopPropagation();
            onAdd();
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onAdd();
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div
            className={kanbanColumnInsertAffordanceClass}
            data-kanban-column-insert-affordance=""
            aria-hidden
          >
            <span className={kanbanColumnInsertLineClass} />
            <span className={kanbanColumnInsertIconClass}>
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
